"use client";

import React, { useEffect, useState } from "react";
import { cn } from "@/lib/utils";
import Image from "next/image";
import ProductCardSkelton from "../common/skeltons/ProductCardSkelton";
import { Category } from "@/types/homePageTypes";
import FadeInUp from "@/components/common/ScrollReveal";
import { useSearchParams, useRouter } from "next/navigation";
import { Suspense } from "react";
import { useProductListingStore } from "@/zustand/productListingStore";
import { useNewGetAllCategories } from "@/hooks/ApiHooks/newProductListingHooks/newGetAllCategoriesHook";

export default function ProductsAndSolutionsWrapper() {
  return (
    <Suspense fallback={<ProductCardSkelton />}>
      <ProductsAndSolutions />
    </Suspense>
  );
}

function ProductsAndSolutions() {
  const searchParams = useSearchParams();
  const router = useRouter();
  const [, setDebouncedSearchTerm] = useState("");
  const [searchTerm] = useState("");
  const { setCategoryIdToStore, setSubCategoryIdToStore } = useProductListingStore();

  useEffect(() => {
    const handle = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm.trim());
    }, 1000);
    return () => clearTimeout(handle);
  }, [searchTerm]);


  const searchCategory = searchParams.get("category") || "All Products";

  const getTabValue = (category: { name?: string; slug?: string }) =>
    (category.slug || category.name || "")
      .toLowerCase()
      .replace(/&amp;/g, "and")
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "");

  const activeCategory = searchCategory;

  const { data: categoryData } = useNewGetAllCategories();

  const processedCategories = (Array.isArray(categoryData?.result) ? categoryData?.result : [])
    .filter((category: Category) =>
      category?.name !== "Accessories" &&
      category?.name !== "Metal Products"
    )
    .sort((a: Category, b: Category) => {
      if (a?.name === "All Products") return -1;
      if (b?.name === "All Products") return 1;
      return 0;
    });

  const currentActiveValue = getTabValue({ name: activeCategory }) ||
    getTabValue(processedCategories?.[0] || {}) ||
    "all-products";

  return (
    <div className="relative py-mob-80 sm:py-tab-90 lg:py-web-90">
      <FadeInUp effect="fadeInUp">
        <div className="max-w-[90.69vw] sm:max-w-[90.80vw] lg:max-w-full mx-auto">
          <div>
            <div className="w-full">
              <h2 className="text-mob-24 sm:text-tab-24 lg:text-web-50  font-medium lg:font-normal leading-[120%] text-TitleBlack text-center">
                Products & Solutions
              </h2>
              <nav 
                aria-label="Product categories"
                className="bg-white z-10 max-w-full lg:flex justify-center pt-mob-46 sm:pt-tab-46 lg:pt-web-75 px-[8vw] lg:px-[18vw]"
              >
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-mob-26 sm:gap-tab-26 lg:gap-x-[9vw] w-full">
                  {
                    processedCategories
                      ? processedCategories
                        .map((category: Category & { slug?: string }) => {
                          const tabValue = getTabValue(category);
                          const isActive = tabValue === currentActiveValue;
                          
                          return (
                            <button
                              key={category.id}
                              type="button"
                              aria-label={`View ${category.name} products`}
                              aria-current={isActive ? "page" : undefined}
                              className={cn(
                                "inline-flex items-center justify-center whitespace-nowrap transition-all focus-visible:outline-none disabled:pointer-events-none disabled:opacity-50 relative after:content-[''] after:absolute after:transition-all after:duration-200 after:ease-in after:h-mob-2 sm:after:h-tab-2 after:bg-PrimaryLightBlue after:bottom-[-1px] after:left-0 pb-mob-15 sm:pb-tab-15 lg:pb-web-27",
                                isActive ? "after:w-full text-foreground" : "after:w-0 text-muted-foreground"
                              )}
                              onClick={() => {
                                const slug = category.slug || category.name?.toLowerCase().replace(/\s+/g, "-");
                                if (slug && category.id !== undefined) {
                                  router.push(`/product-category/${slug}`);
                                  setCategoryIdToStore(Number(category.id));
                                  setSubCategoryIdToStore(0);
                                }
                              }}
                            >
                              <div className="flex flex-col mb-auto items-center gap-mob-30 sm:gap-tab-10 lg:gap-web-26 h-fit">
                                <div className="w-mob-60 sm:w-tab-50 lg:w-web-70 h-mob-60 sm:h-tab-32 lg:h-web-65">
                                  {category.thumbnail ? (
                                    <Image
                                      src={category.thumbnail}
                                      alt=""
                                      aria-hidden="true"
                                      fill
                                      sizes="(max-width: 991px) 60px, 70px"
                                      className="!static !h-full object-contain object-center"
                                    />
                                  ) : (
                                    <Image
                                      src="/images/drywall-partition.svg"
                                      alt=""
                                      aria-hidden="true"
                                      fill
                                      sizes="(max-width: 991px) 60px, 70px"
                                      className="!static !h-auto"
                                    />
                                  )}
                                </div>

                                <span className="block text-[#323232] text-mob-15 sm:text-tab-15 lg:text-web-20 leading-[150%] font-medium whitespace-normal text-left lg:text-center">
                                  {category.name
                                    .replace(/&amp;/g, "&")
                                    .split(/(&|\/|\band\b)/i)
                                    .map((part, index) => (
                                      <React.Fragment key={index}>
                                        <span>
                                          {part.trim()}
                                        </span>
                                        {part.match(/&|\/|\band\b/i) && (
                                          <br />
                                        )}{" "}
                                      </React.Fragment>
                                    ))}
                                </span>
                              </div>
                            </button>
                          );
                        })
                      : ""
                  }
                </div>
              </nav>
            </div>
          </div>
        </div>
      </FadeInUp>
    </div>
  );
}
